import s from './Hero.scss';
import React from 'react';
import {Image, ImageResizeOptions, Text, TextTypography} from 'wix-ui-tpa/cssVars';
import {withGlobals} from '../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../gallery/galleryGlobalStrategy';
import classNames from 'classnames';
import {HeadingTags} from '@wix/wixstores-client-core/dist/es/src/types/heading-tags';
import {classes as stylableClasses, st} from './Hero.st.css';
import {useStyles} from '@wix/tpa-settings/react';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useStylesParams} from '../../../stylesParamsContext';
import {Clamp} from '../../../common/components/Clamp/Clamp';
import {CategoryHeaderLayout} from '../../../constants';

export enum HeroDataHook {
  Root = 'HeroDataHook.Root',
  HeroImage = 'HeroDataHook.HeroImage',
  CategoryName = 'HeroDataHook.CategoryName',
  Description = 'HeroDataHook.Description',
}

type HeroProps = IGalleryGlobalProps & IProvidedTranslationProps;

export const Hero: React.FunctionComponent<HeroProps> = withGlobals(
  withTranslations()((props: HeroProps) => {
    const styles = useStyles();
    const stylesParams = useStylesParams();
    const shouldShowCategoryHeaderReadMoreLink = styles.get(stylesParams.gallery_showCategoryHeaderReadMoreLink);
    const {t} = props;
    const {isSEO, shouldShowMobile, handleCategoryClampClick} = props.globals;

    const {name: categoryName, description: categoryDescription, media} = props.globals.currentCategory;
    const shouldShowImage = styles.get(stylesParams.gallery_showCategoryHeaderImage) && media?.url;
    const shouldShowName = styles.get(stylesParams.gallery_showCategoryHeaderName);
    const shouldShowDescription = styles.get(stylesParams.gallery_showCategoryHeaderDescription) && categoryDescription;
    const shouldShowNameAndDescriptionContainer = shouldShowName || shouldShowDescription;
    const layout = styles.get(stylesParams.gallery_categoryHeaderLayout);

    const renderClampedDescription = () => {
      return (
        <Clamp
          className={classNames(s.descriptionContainer, {[s.nameVisible]: shouldShowName})}
          readMoreLessLinkClassName={classNames(
            st(stylableClasses.readMoreLinkRoot, {useMobileFont: shouldShowMobile})
          )}
          text={categoryDescription}
          enabled={shouldShowCategoryHeaderReadMoreLink}
          dataHook={HeroDataHook.Description}
          maxLines={shouldShowMobile ? 3 : 2}
          readMoreText={t('categoryHeaderReadMoreLink')}
          readLessText={t('categoryHeaderReadLessLink')}
          handleClampClick={handleCategoryClampClick}>
          {categoryDescription}
        </Clamp>
      );
    };

    const getLayoutClass = () => {
      if (layout === CategoryHeaderLayout.TextInside && shouldShowImage) {
        return s.textInside;
      }

      if (layout === CategoryHeaderLayout.TextBelow) {
        return s.textBelow;
      }

      return s.textBelow;
    };

    const getCategoryName = () => {
      return categoryName === 'All Products' ? t('allProducts') : categoryName;
    };

    return (
      <div className={classNames(s.root, getLayoutClass())} data-hook={HeroDataHook.Root}>
        {shouldShowImage && (
          <div className={s.imageContainer}>
            <Image
              className={stylableClasses.imageRoot}
              data-hook={HeroDataHook.HeroImage}
              src={media.url}
              sourceWidth={media.width}
              sourceHeight={media.height}
              resize={ImageResizeOptions.cover}
              isSEOBot={isSEO}
              shouldUseLQIP={true}
              fluid
            />
          </div>
        )}
        {shouldShowNameAndDescriptionContainer && (
          <div className={classNames(s.categoryInfo, {[s.imageVisible]: shouldShowImage})}>
            <div className={s.nameAndDescriptionContainer}>
              {shouldShowName && (
                <Text
                  tagName={HeadingTags.H1}
                  typography={TextTypography.smallTitle}
                  className={classNames(
                    s.categoryName,
                    st(stylableClasses.categoryNameRoot, {useMobileFont: shouldShowMobile})
                  )}
                  data-hook={HeroDataHook.CategoryName}>
                  {getCategoryName()}
                </Text>
              )}

              {shouldShowDescription && renderClampedDescription()}
            </div>
          </div>
        )}
      </div>
    );
  })
);
